/**
 * Module-level singleton tracking the currently active studio.
 *
 * Set when the room route mounts, cleared on unmount.
 * Read by resolveAnalyticsContext to auto-inject studio_id
 * into all events fired within a studio context.
 */
export const ActiveStudio: { id: string | undefined } = { id: undefined };

// Change listeners let React observe room entry/exit via useSyncExternalStore.
// Room entry is signalled only through setActiveStudio (the room route calls it
// on mount) — it is not derivable from any depot field, so without a signal
// here a hook that keys PostHog group identification on the active studio
// would miss the mount moment entirely. [ENG-4401]
const listeners = new Set<() => void>();

const notifyActiveStudioChanged = (): void => {
  for (const listener of listeners) {
    listener();
  }
};

export const setActiveStudio = (id: string): void => {
  // Dedupe: the room route's effect can re-run without the room changing;
  // notifying on a no-op assignment would wake subscribers for nothing.
  if (ActiveStudio.id === id) {
    return;
  }
  ActiveStudio.id = id;
  notifyActiveStudioChanged();
};

export const clearActiveStudio = (): void => {
  if (ActiveStudio.id === undefined) {
    return;
  }
  ActiveStudio.id = undefined;
  notifyActiveStudioChanged();
};

/** Subscribe to active-studio changes; returns the unsubscribe function. */
export const subscribeActiveStudio = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** Snapshot read for useSyncExternalStore (must be stable between changes). */
export const getActiveStudioId = (): string | undefined => ActiveStudio.id;
